import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from '@/store';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

//import './index.scss';

type ResizeProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const Resize = (componentProps: ResizeProps) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  let defaultWidth = 0;
  let defaultHeight = 0;

  if (props?.properties?.elementSelector) {
    const element = document.querySelector(props.properties.elementSelector);
    if (element) {
      defaultWidth = element.clientWidth;
      defaultHeight = element.clientHeight;
    }
  }

  const [state, setState] = React.useState({
    [props?.properties?.widthKey ?? 'width']: props?.properties?.elementSelector ? defaultWidth : window.innerWidth,
    [props?.properties?.heightKey ?? 'height']: props?.properties?.elementSelector ? defaultHeight : window.innerHeight,
  });

  React.useEffect(() => {
    let resizeObserver: ResizeObserver | void | null = null;
    let handleResize: any = 0;
    let element: any = null;

    if (props?.properties?.elementSelector) {
      element = document.querySelector(props.properties.elementSelector);
      if (element) {
        resizeObserver = new ResizeObserver(([change]) => {
          setState((v) => {
            const widthKey = props?.properties?.widthKey ?? 'width';
            const heightKey = props?.properties?.heightKey ?? 'height';

            if (v[widthKey] === change.contentRect.width && v[heightKey] === change.contentRect.height) {
              return v; // No change, return the current state
            }

            element.style.setProperty(`--${widthKey}`, change.contentRect.width.toString());
            element.style.setProperty(`--${heightKey}`, change.contentRect.height.toString());

            return {
              [widthKey]: change.contentRect.width,
              [heightKey]: change.contentRect.height,
            };
          });
        }).observe(element);
      }
    } else {
      // If no elementSelector is provided, we will use the window resize
      const widthKey = props?.properties?.widthKey ?? 'width';
      const heightKey = props?.properties?.heightKey ?? 'height';

      handleResize = () => {
        setState((v) => {
          if (v[widthKey] === window.innerWidth && v[heightKey] === window.innerHeight) {
            return v; // No change, return the current state
          }

          document.documentElement.style.setProperty(`--${widthKey}`, window.innerWidth.toString());
          document.documentElement.style.setProperty(`--${heightKey}`, window.innerHeight.toString());

          return {
            widthKey: window.innerWidth,
            heightKey: window.innerHeight,
          };
        });
      };

      window.addEventListener('resize', handleResize);

      document.documentElement.style.setProperty(`--${widthKey}`, window.innerWidth.toString());
      document.documentElement.style.setProperty(`--${heightKey}`, window.innerHeight.toString());
    }

    // Cleanup the event listener on component unmount
    return () => {
      const widthKey = props?.properties?.widthKey ?? 'width';
      const heightKey = props?.properties?.heightKey ?? 'height';

      if (element) element.style.removeProperty(`--${widthKey}`);
      if (element) element.style.removeProperty(`--${heightKey}`);
      if (resizeObserver) resizeObserver.disconnect();
      if (handleResize) window.removeEventListener('resize', handleResize);
      document.documentElement.style.setProperty(`--${widthKey}`, window.innerWidth.toString());
      document.documentElement.style.setProperty(`--${heightKey}`, window.innerHeight.toString());
    };
  }, []); // eslint-disable-line

  //console.log('Resize[contextValue]', state, props.properties.elementSelector);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={state}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Resize;
